import { GatewayApiClient, setDefaultClient } from '@ifit/gateway-core';
import axios, { AxiosRequestConfig } from 'axios';
import b64 from 'base64-js';
import promiseRetry from 'promise-retry';
import { WeakType } from '../typescript-migration';
import { removeStartingSlash, removeTrailingSlash } from './string-helpers';

const DEFAULT_RETRY_OPTIONS = {
  retries: 1,
  maxTimeout: 30e3,
};

type HttpMethod = 'get' | 'put' | 'post' | 'delete';

export interface IRequestOptions extends Partial<AxiosRequestConfig> {
  url?: string;
  method?: HttpMethod;
  headers?: WeakType;
  useBasicAuth?: boolean;
}

interface IClientOpts {
  token: string;
  clientId: string;
  clientSecret: string;
  baseUrl: string;
  language: string;
  retryOptions?: WeakType;
}

interface IClientConfig extends Omit<IClientOpts, 'clientId' | 'clientSecret'> {
  basicAuthHeader: string;
}

interface IHTTPOptions extends IClientConfig, IRequestOptions {}

export class Client {
  config: IClientConfig;

  constructor({
    token,
    clientId,
    clientSecret,
    baseUrl,
    language,
    retryOptions = {},
  }: IClientOpts) {
    this.config = {
      basicAuthHeader: getBasicAuthToken({
        username: clientId,
        password: clientSecret,
      }),
      token,
      baseUrl,
      language,
      retryOptions: { ...DEFAULT_RETRY_OPTIONS, ...retryOptions },
    };
    const gatewayBaseUrl = baseUrl.split('wolf-athenaeum-service')[0];
    setDefaultClient(
      new GatewayApiClient({
        baseURL: gatewayBaseUrl,
        cacheURL: gatewayBaseUrl.replace('/gateway.', '/gateway-cache.'),
        baseServiceURL: process.env.REACT_APP_BASE_SERVICE_URL,
        privateToken: token ? 'Bearer ' + token : undefined,
        clientId,
        clientSecret,
        status: { statusEndpoint: '', enableStatusChecks: false },
      }),
    );
  }

  public get = (url: string, options: IRequestOptions) =>
    this.makeRequest({
      ...options,
      url,
      method: 'get',
    });
  public put = (url: string, options: IRequestOptions) =>
    this.makeRequest({
      ...options,
      url,
      method: 'put',
    });
  public post = (url: string, options: IRequestOptions) =>
    this.makeRequest({
      ...options,
      url,
      method: 'post',
    });
  public delete = (url: string, options: IRequestOptions) =>
    this.makeRequest({
      ...options,
      url,
      method: 'delete',
    });

  public makeRequest = (options: IRequestOptions) =>
    withRetry(
      () => internalMakeRequest({ ...this.config, ...options }),
      this.config.retryOptions,
    );
}

function getRequestUrl({ baseUrl, url }): string {
  return baseUrl
    ? `${removeTrailingSlash(baseUrl)}/${removeStartingSlash(url)}`
    : url;
}

async function internalMakeRequest({
  baseUrl,
  url,
  token,
  language,
  basicAuthHeader,
  headers,
  useBasicAuth,
  ...options
}: IHTTPOptions) {
  const useBearerToken = token && (!useBasicAuth || !basicAuthHeader);
  if (!useBearerToken && window.NODE_ENV === 'production') {
    baseUrl = baseUrl.replace('/gateway.', '/gateway-cache.');
  }
  const requestUrl = getRequestUrl({ baseUrl, url });
  const authHeader = useBearerToken ? `Bearer ${token}` : basicAuthHeader;
  const requestHeaders = {
    'Content-Type': 'application/json',
    Authorization: authHeader,
    'Accept-Language': language,
    ...headers,
  };

  const axiosOptions = {
    ...options,
    url: requestUrl,
    headers: requestHeaders,
    validateStatus,
  };

  const response = await axios(axiosOptions);

  const responseOK =
    response &&
    ((response.status === 200 && response.statusText === '') ||
      response.statusText === 'OK' ||
      response.statusText === 'HTTP/2.0 200');
  if (!responseOK) {
    throw new Error(
      response
        ? `${response.status}: ${response.statusText} when requesting ${response.data}`
        : `Error when requesting ${url}`,
    );
  }

  return response;
}

export function withRetry(
  promiseFn: () => WeakType,
  options,
): Promise<WeakType> {
  return promiseRetry(
    retry =>
      promiseFn().catch(err => {
        return retry(err);
      }),
    {
      ...DEFAULT_RETRY_OPTIONS,
      ...options,
    },
  );
}

function getBasicAuthToken({
  username,
  password,
}: {
  username: string;
  password: string;
}): string {
  if (!username || !password) return null;
  const generatedToken = b64.fromByteArray(
    new Uint8Array(Buffer.from(`${username}:${password}`)),
  );
  return `Basic ${generatedToken}`;
}

export function validateStatus(status: number): boolean {
  return (status >= 200 && status < 300) || status === 304;
}
